import { useMemo } from 'react'
import { useForm } from '@inertiajs/react'
import { CreditCard, Gift, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { BillingPlanOption, DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import { cn } from '@/lib/utils'

interface Props {
  worker: DigitalWorkerDetail
  plans: BillingPlanOption[]
  disabled?: boolean
  canGrantAdminSubscription?: boolean
  onContinue: () => void
}

function formatCurrency(cents: number, currency: string) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(cents / 100)
}

export default function SubscribeStep({
  worker,
  plans,
  disabled = false,
  canGrantAdminSubscription = false,
  onContinue,
}: Props) {
  const purchasablePlans = useMemo(() => plans.filter((plan) => plan.purchasable), [plans])
  const defaultSlug = purchasablePlans[0]?.slug ?? ''
  const form = useForm({ plan_slug: defaultSlug })
  const compedForm = useForm({})
  const billing = worker.billing
  const selectedPlan = purchasablePlans.find((plan) => plan.slug === form.data.plan_slug)
  const adminCompedPlan = plans.find((plan) => plan.published)

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!selectedPlan || billing.active) return

    form.post(`/dashboard/digital_workers/${worker.id}/checkout`, {
      preserveScroll: true,
    })
  }

  const handleGrantCompedSubscription = () => {
    if (!canGrantAdminSubscription || billing.active || !adminCompedPlan) return

    compedForm.post(`/dashboard/digital_workers/${worker.id}/grant_comped_subscription`, {
      preserveScroll: true,
      preserveState: false,
    })
  }

  if (billing.active) {
    return (
      <div className="space-y-5" data-testid="onboarding-subscription-active">
        <div className="space-y-1.5">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">Assinatura ativa</h2>
          <p className="text-sm text-muted-foreground">
            {billing.source === 'manual'
              ? `Cortesia ativa no plano ${billing.plan_name}.`
              : `Plano ${billing.plan_name} confirmado.`}
          </p>
        </div>
        <Button type="button" onClick={onContinue} disabled={disabled}>
          Continuar
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="onboarding-subscription-stage">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">Escolha o plano de cobrança</h2>
        <p className="text-sm text-muted-foreground">
          O colaborador fica pendente até a assinatura ser ativada.
        </p>
      </div>

      {purchasablePlans.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Nenhum plano publicado com preço Stripe está disponível no momento.
        </div>
      ) : (
        <div className="grid gap-3" role="radiogroup" aria-label="Planos de cobrança">
          {purchasablePlans.map((plan) => {
            const selected = form.data.plan_slug === plan.slug
            return (
              <button
                type="button"
                key={plan.slug}
                onClick={() => form.setData('plan_slug', plan.slug)}
                className={cn(
                  'flex items-start justify-between gap-4 rounded-lg border p-4 text-left transition',
                  selected ? 'border-violet-500 bg-violet-50/70 dark:bg-violet-500/10' : 'border-border bg-background hover:bg-accent',
                )}
                aria-checked={selected}
                role="radio"
              >
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                    <CreditCard className="h-4 w-4" />
                    {plan.name}
                  </span>
                  {plan.description && (
                    <span className="mt-1 block text-sm text-muted-foreground">{plan.description}</span>
                  )}
                </span>
                <span className="whitespace-nowrap text-sm font-semibold text-foreground">
                  {formatCurrency(plan.price_cents, plan.currency)}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {canGrantAdminSubscription && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 font-medium">
                <Gift className="h-4 w-4" />
                Cortesia administrativa
              </div>
              <p className="mt-1 text-emerald-800/80 dark:text-emerald-100/80">
                {adminCompedPlan
                  ? `Ativa assinatura manual no plano ${adminCompedPlan.name} e continua sem abrir Stripe.`
                  : 'Nenhum plano publicado está disponível para ativar cortesia.'}
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={handleGrantCompedSubscription}
              disabled={disabled || compedForm.processing || !adminCompedPlan}
              className="shrink-0 bg-background/80"
            >
              {compedForm.processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gift className="h-4 w-4" />}
              Continuar sem Stripe
            </Button>
          </div>
        </div>
      )}

      {billing.checkout_session_status === 'open' && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
          <Gift className="h-4 w-4" />
          Checkout iniciado. Você pode abrir outro checkout para substituir a sessão anterior.
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={disabled || form.processing || !selectedPlan}>
          {form.processing && <Loader2 className="h-4 w-4 animate-spin" />}
          Ir para o Stripe
        </Button>
      </div>
    </form>
  )
}
